import { astroMapQuiz } from "./config/astro-map-quiz"
import { auraScanQuiz } from "./config/aura-scan-quiz"
import { compatibilityQuiz } from "./config/compatibility-quiz"
import { demoQuiz } from "./config/demo-quiz"
import { palmistryQuiz } from "./config/palmistry-quiz"
import { simplifiedQuiz } from "./config/simplified-quiz"
import { soulmateSketchQuiz } from "./config/soulmate-sketch-quiz"
import type { QuizConfig } from "./types"

/**
 * Maps a funnel key to a quiz config. Adding a new quiz = one entry here; no
 * routing or renderer changes. Keys can encode versions (e.g. `palmistry_v2`)
 * so variants coexist without breaking older configs.
 *
 * `satisfies` keeps the literal keys inferred (so `QuizKey` stays exact) while
 * still type-checking every value as a `QuizConfig`.
 */
const quizRegistry = {
  palmistry: palmistryQuiz,
  demo: demoQuiz,
  compatibility: compatibilityQuiz,
  simplified: simplifiedQuiz,
  "soulmate-sketch": soulmateSketchQuiz,
  "astro-map": astroMapQuiz,
  "aura-scan": auraScanQuiz,
} satisfies Record<string, QuizConfig>

/** Every registered quiz key, as a literal union (derived from the registry). */
export type QuizKey = keyof typeof quizRegistry

/** All registered quiz keys. The single source of truth is `quizRegistry`. */
export const QUIZ_KEYS = Object.keys(quizRegistry) as QuizKey[]

/** Narrow an arbitrary param value to a known quiz key. */
export function isQuizKey(key: string): key is QuizKey {
  return key in quizRegistry
}

/** Quiz used when the funnel names none, or names an unknown one. */
export const DEFAULT_QUIZ_KEY: QuizKey = "simplified"

/** Param key that carries the quiz selector (mirrors `checkout_v`). */
const QUIZ_PARAM_KEYS = ["quiz_v"] as const

/** The quiz key requested by the funnel params, if any. */
export function getRequestedQuizKey(params: Record<string, string | undefined>): string | null {
  for (const key of QUIZ_PARAM_KEYS) {
    const value = params[key]
    if (value && value.trim()) return value.trim()
  }
  return null
}

export interface ResolvedQuiz {
  /** Registry key of the resolved quiz. */
  id: string
  config: QuizConfig
  /** True when a quiz was requested but not found, so the default was used. */
  fellBack: boolean
}

/**
 * Resolve which quiz to render from the effective funnel params, falling back
 * to the default for an unknown/absent key. Pure — side-effect-free for tests.
 */
export function resolveQuiz(params: Record<string, string | undefined>): ResolvedQuiz {
  const requested = getRequestedQuizKey(params)
  if (requested && isQuizKey(requested)) {
    return { id: requested, config: quizRegistry[requested], fellBack: false }
  }
  return {
    id: DEFAULT_QUIZ_KEY,
    config: quizRegistry[DEFAULT_QUIZ_KEY],
    fellBack: requested !== null,
  }
}
